import React, { useState, useEffect, useCallback } from 'react';
import { Task, TasksResponse, tasksApi } from '../../lib/tasks-api';
import { toast } from '../../lib/toast';
import { TaskErstellenModal } from './TaskErstellenModal';
import { TaskEditModal } from './TaskEditModal';
import { MIcon } from '../../lib/micon';
import { QuickMicButton } from '../diktat/QuickMicButton';
import { oeffneKlassenarbeitListe } from '../../lib/nav';
import { heuteIso, wtKommaTm, tm } from '../../features/schueler/profil/format';
import '../../features/klasse/klasse.css';
import '../../features/meintag/meintag.css';

// Welle K3: Herkunft statt Abzeichen — nur wenn nicht von Hand angelegt
const HERKUNFT: Partial<Record<Task['quelle'], string>> = {
  diktat: 'aus Erzählen',
  gespraech: 'aus Gespräch',
  mail: 'aus Mail',
  radar: 'aus einem Hinweis auf Heute',
  klassenarbeit: 'zur Klassenarbeit',
};

const tageZwischen = (a: string, b: string) => Math.round((new Date(b + 'T12:00:00Z').getTime() - new Date(a + 'T12:00:00Z').getTime()) / 86400000);

/** „seit 19.10. fällig“, „seit gestern fällig“, „heute“, „morgen“, „Fr, 06.11.“ */
function faelligText(faellig: string, heute: string): { text: string; ueberfaellig: boolean } {
  const d = faellig.slice(0, 10);
  const diff = tageZwischen(heute, d);
  if (diff < -1) return { text: `seit ${tm(d)} fällig`, ueberfaellig: true };
  if (diff === -1) return { text: 'seit gestern fällig', ueberfaellig: true };
  if (diff === 0) return { text: 'heute', ueberfaellig: false };
  if (diff === 1) return { text: 'morgen', ueberfaellig: false };
  return { text: wtKommaTm(d), ueberfaellig: false };
}

// Hoch-Priorität zuerst, dann nach Fälligkeit
function prioSort(arr: Task[]): Task[] {
  return [...arr].sort((a, b) => {
    const pa = a.prioritaet === 'hoch' ? 0 : 1;
    const pb = b.prioritaet === 'hoch' ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return (a.faellig_am ?? '9999-99-99').localeCompare(b.faellig_am ?? '9999-99-99');
  });
}

// ── Zeile: runder Haken, Titel, Metazeile (Fälligkeit, Herkunft, Kind/Klasse als Chip) ──
function TaskRow({ task, onToggle, onEdit }: { task: Task; onToggle: (task: Task) => void; onEdit: (task: Task) => void }) {
  const erledigt = task.erledigt === 1;
  const heute = heuteIso();
  const f = task.faellig_am && !erledigt ? faelligText(task.faellig_am, heute) : null;
  const herkunft = task.quelle === 'gespraech' && task.gespraech_datum
    ? `aus Gespräch vom ${task.gespraech_datum.slice(8, 10)}.${task.gespraech_datum.slice(5, 7)}.`
    : HERKUNFT[task.quelle];
  const kind = task.schueler_id != null ? `${task.schueler_vorname ?? 'Kind'} ${task.schueler_nachname ?? ''}`.trim() : null;
  return (
    <div className={`ld-ta-zeile${erledigt ? ' ist-erledigt' : ''}`}>
      <button type="button" className="ld-ta-haken" onClick={() => onToggle(task)}
        aria-pressed={erledigt} aria-label={erledigt ? `${task.titel}: als offen markieren` : `${task.titel}: als erledigt markieren`}>
        <span>{erledigt && <MIcon name="check" size={15} weight={700} />}</span>
      </button>
      <button type="button" className="ld-ta-text" onClick={() => onEdit(task)}>
        <b>{task.prioritaet === 'hoch' && !erledigt && <MIcon name="flag" size={15} fill className="ld-ta-flagge" label="wichtig" />}{task.titel}</b>
        {(f || (herkunft && !erledigt) || kind || task.klasse_name) && (
          <span className="ld-ta-meta">
            {f && <span className={f.ueberfaellig ? 'ld-ta-faellig--ueber' : undefined}>{f.text}</span>}
            {herkunft && !erledigt && <span>{herkunft}</span>}
            {kind && <span className="ld-ta-chip">{kind}</span>}
            {task.klasse_id != null && task.klasse_name && <span className="ld-ta-chip">{task.klasse_name}</span>}
          </span>
        )}
      </button>
    </div>
  );
}

function Gruppe({ titel, tasks, onToggle, onEdit }: { titel: string; tasks: Task[]; onToggle: (t: Task) => void; onEdit: (t: Task) => void }) {
  if (tasks.length === 0) return null;
  return (
    <>
      <h2 className="ld-kl-abschnitt">{titel} <span className="ld-kl-abschnitt__zahl">{tasks.length}</span></h2>
      <div className="ld-kl-karte">
        {tasks.map(t => <TaskRow key={t.id} task={t} onToggle={onToggle} onEdit={onEdit} />)}
      </div>
    </>
  );
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
// Welle K3: ein Hauptknopf „+ Aufgabe“ (Mikro als kleines Symbol), keine Kacheln. Gruppen Überfällig, Heute,
// danach „Später n“ (aufklappbar) und „Zuletzt erledigt n“ (aufklappbar; der Server liefert nur die letzten 7 Tage). Daten und Abläufe unverändert.
export function TasksAnsicht() {
  const [response, setResponse]     = useState<TasksResponse | null>(null);
  const [laden, setLaden]           = useState(true);
  const [erstellenOffen, setErstellenOffen] = useState(false);
  const [editTask, setEditTask]     = useState<Task | null>(null);
  const [spaeterOffen, setSpaeterOffen] = useState(false);
  const [erledigtOffen, setErledigtOffen] = useState(false);

  const laden_ = useCallback(async () => {
    try {
      const data = await tasksApi.laden();
      setResponse(data);
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { laden_(); }, [laden_]);
  // Welle B2: „Noten eintragen: Klassenarbeit …“ öffnet die Liste der Klasse statt des Bearbeitens;
  // nach dem Speichern der Liste ist die Aufgabe erledigt → neu laden
  const oeffneTask = (t: Task) => (t.quelle === 'klassenarbeit' && t.quelle_ref_id
    ? oeffneKlassenarbeitListe({ terminId: Number(t.quelle_ref_id), taskId: t.id })
    : setEditTask(t));
  useEffect(() => {
    const h = () => { laden_(); };
    window.addEventListener('ld-noten-geaendert', h);
    return () => window.removeEventListener('ld-noten-geaendert', h);
  }, [laden_]);

  async function toggleErledigt(task: Task) {
    const neuErledigt = task.erledigt === 0 ? 1 : 0;
    setResponse(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks.map(t => t.id === task.id ? { ...t, erledigt: neuErledigt as 0 | 1 } : t);
      return { ...prev, tasks };
    });
    try {
      await tasksApi.aktualisieren(task.id, { erledigt: neuErledigt as 0 | 1 });
      laden_();
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
      laden_();
    }
  }

  const g = response?.gruppen;
  const ueberfaellig = g ? prioSort(g.ueberfaellig) : [];
  const heute        = g ? prioSort(g.heute) : [];
  const spaeter      = g ? [...prioSort([...g.diese_woche, ...g.spaeter]), ...prioSort(g.undatiert)] : [];
  const erledigt     = g?.erledigt ?? [];
  const nichtsSichtbar = !laden && ueberfaellig.length + heute.length + spaeter.length + erledigt.length === 0;

  return (
    <div>
      <div className="ld-ma-knoepfe" style={{ marginBottom: 6 }}>
        <button type="button" className="ld-kl-haupt" onClick={() => setErstellenOffen(true)}><MIcon name="add" size={19} />Aufgabe</button>
        <QuickMicButton modul="task" size="sm" />
      </div>

      {laden && <div className="ld-kl-karte ld-kt-leer">Lädt …</div>}
      {nichtsSichtbar && <div className="ld-kl-karte ld-kt-leer">Keine Aufgaben. Lege mit „+ Aufgabe“ die erste an.</div>}
      {g && !laden && ueberfaellig.length + heute.length === 0 && !nichtsSichtbar && (
        <div className="ld-kl-karte ld-kt-leer" style={{ marginTop: 12 }}>Für heute ist nichts mehr offen.</div>
      )}

      {g && (
        <>
          <Gruppe titel="Überfällig" tasks={ueberfaellig} onToggle={toggleErledigt} onEdit={oeffneTask} />
          <Gruppe titel="Heute" tasks={heute} onToggle={toggleErledigt} onEdit={oeffneTask} />

          {(spaeter.length > 0 || erledigt.length > 0) && (
            <div className="ld-kl-karte ld-ta-mehr">
              {spaeter.length > 0 && (
                <button type="button" className="ld-ta-mehr__knopf" aria-expanded={spaeterOffen} onClick={() => setSpaeterOffen(o => !o)}>
                  <MIcon name="event_upcoming" size={20} /><span>Später <em>{spaeter.length}</em></span>
                  <MIcon name={spaeterOffen ? 'expand_less' : 'expand_more'} size={20} />
                </button>
              )}
              {spaeterOffen && spaeter.map(t => <TaskRow key={t.id} task={t} onToggle={toggleErledigt} onEdit={oeffneTask} />)}
              {erledigt.length > 0 && (
                <button type="button" className={`ld-ta-mehr__knopf${spaeter.length > 0 ? ' ld-kl-trenn' : ''}`} aria-expanded={erledigtOffen} onClick={() => setErledigtOffen(o => !o)}>
                  <MIcon name="task_alt" size={20} /><span>Zuletzt erledigt <em>{erledigt.length}</em></span>
                  <MIcon name={erledigtOffen ? 'expand_less' : 'expand_more'} size={20} />
                </button>
              )}
              {erledigtOffen && erledigt.map(t => <TaskRow key={t.id} task={t} onToggle={toggleErledigt} onEdit={oeffneTask} />)}
            </div>
          )}
        </>
      )}

      <TaskErstellenModal
        open={erstellenOffen}
        onClose={() => setErstellenOffen(false)}
        onErstellt={() => { laden_(); toast('Aufgabe erstellt', 'success'); }}
        quelle="manuell"
      />

      <TaskEditModal
        task={editTask}
        onClose={() => setEditTask(null)}
        onAktualisiert={() => { laden_(); toast('Aufgabe gespeichert', 'success'); }}
        onGeloescht={() => { laden_(); toast('Aufgabe gelöscht', 'success'); }}
      />
    </div>
  );
}
